import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Sparkles, Clock, Target, Library, ArrowRight, GraduationCap, Award, TrendingDown, Zap, PiggyBank } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const CountUp = ({ end, duration = 1600, prefix = "", suffix = "" }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const step = (now) => {
            const p = Math.min(1, (now - t0) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(end * eased));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      });
    }, { threshold: 0.4 });
    io.observe(node);
    return () => io.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{prefix}{val.toLocaleString("en-IN")}{suffix}</span>;
};

const EXAM_COLORS = {
  JEE: "bg-blue-100 text-blue-800",
  NEET: "bg-emerald-100 text-emerald-800",
  UPSC: "bg-amber-100 text-amber-800",
  CAT: "bg-purple-100 text-purple-800",
  SSC: "bg-rose-100 text-rose-800",
};

const TopperCard = ({ t, i }) => (
  <div className="fade-up group relative bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-slate-900 transition-colors p-6 flex flex-col justify-between gap-4"
    style={{ animationDelay: `${i * 70}ms` }} data-testid={`topper-card-${t.id}`}>
    <div>
      <div className="flex items-start justify-between gap-2">
        <Badge className={`${EXAM_COLORS[t.exam] || "bg-slate-100 text-slate-800"} hover:${EXAM_COLORS[t.exam] || "bg-slate-100"} font-mono text-[10px] uppercase tracking-widest`}>{t.exam} · {t.year}</Badge>
        {t.exam !== "JEE" && (
          <div className="font-mono text-xs text-amber-700 inline-flex items-center gap-1.5">
            <Trophy className="h-3.5 w-3.5" />
            {t.rank}
          </div>
        )}
      </div>
      <div className="font-heading text-xl font-bold text-slate-900 mt-4 leading-tight">{t.name}</div>
      {t.college && <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5" />{t.college}</div>}
    </div>
    {t.quote && <p className="text-sm text-slate-600 leading-relaxed italic border-l-2 border-amber-300 pl-3">"{t.quote}"</p>}
  </div>
);

export default function HomePage() {
  const { user } = useAuth();
  const [toppers, setToppers] = useState([]);
  useEffect(() => { api.get("/toppers").then((r) => setToppers(r.data)).catch(() => {}); }, []);

  const dest = user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/signup";
  const byExam = toppers.reduce((acc, t) => { (acc[t.exam] ||= []).push(t); return acc; }, {});

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <nav className="flex items-center gap-2">
            {!user && <Link to="/login"><Button variant="ghost" data-testid="home-login-link" className="hover:bg-slate-900 hover:text-white">Sign in</Button></Link>}
            {user && <Link to={user.role === "admin" ? "/admin" : "/dashboard"}><Button variant="ghost" data-testid="home-dashboard-link" className="hover:bg-slate-900 hover:text-white">Dashboard</Button></Link>}
            <Link to={dest}><Button data-testid="home-cta-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white text-white">{user ? "Continue" : "Start free · ₹69"}<ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
          </nav>
        </div>
      </header>

      {/* Pricing comparison — running numbers (top of page) */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20" data-testid="home-pricing-compare">
          <div className="small-label fade-up flex items-center gap-2"><PiggyBank className="h-3 w-3" />Pricing · you vs them</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-2 fade-up max-w-3xl">The same mocks, at <span className="text-emerald-600">1/70<sup className="text-xl">th</sup></span> the price.</h2>
          <p className="text-slate-500 mt-4 max-w-2xl fade-up">Compare what the biggest mock-test platforms charge for one year of JEE/NEET prep against our one simple ₹69.</p>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            <div className="fade-up relative border border-slate-200 rounded-2xl p-7 bg-slate-50 overflow-hidden" data-testid="pricing-others">
              <div className="flex items-center gap-2 small-label text-slate-500"><TrendingDown className="h-3 w-3" />Others charge up to</div>
              <div className="font-heading text-6xl font-bold tracking-tight mt-4 text-slate-400 line-through decoration-red-400 decoration-[3px]" data-testid="pricing-others-amount">
                <CountUp end={7500} prefix="₹" duration={1800} />
              </div>
              <div className="text-sm text-slate-500 mt-3">Allen · Aakash · PW · Unacademy / per year</div>
            </div>

            <div className="fade-up relative border-2 border-emerald-500 rounded-2xl p-7 bg-emerald-50 overflow-hidden" data-testid="pricing-ours" style={{ animationDelay: "120ms" }}>
              <div className="flex items-center gap-2 small-label text-emerald-700"><Zap className="h-3 w-3" />You pay only</div>
              <div className="font-heading text-6xl font-bold tracking-tight mt-4 text-emerald-700" data-testid="pricing-ours-amount">
                <CountUp end={69} prefix="₹" duration={1200} />
              </div>
              <div className="text-sm text-emerald-800 mt-3">Per Month · Pinslib activation</div>
            </div>

            <div className="fade-up relative border border-slate-900 rounded-2xl p-7 bg-slate-900 text-white overflow-hidden" data-testid="pricing-save" style={{ animationDelay: "240ms" }}>
              <div className="flex items-center gap-2 small-label text-amber-300"><PiggyBank className="h-3 w-3" />You save</div>
              <div className="font-heading text-6xl font-bold tracking-tight mt-4 text-amber-400" data-testid="pricing-save-amount">
                <CountUp end={7431} prefix="₹" duration={2000} />
              </div>
              <div className="text-sm text-slate-300 mt-3">That's <span className="font-mono text-amber-300"><CountUp end={99} suffix="%" duration={1600} /></span> cheaper than the big names.</div>
            </div>
          </div>

          <div className="mt-6 text-xs text-slate-400 fade-up">* Prices sourced from publicly listed JEE/NEET mock-test plans on competitor sites (Nov 2025).</div>
        </div>
      </section>

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0F172A] text-white">
        <div className="absolute inset-0 opacity-20 grid-bg" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative">
          <div className="max-w-3xl fade-up">
            <Badge className="bg-amber-500/90 text-amber-950 hover:bg-amber-500/90 font-mono text-[11px] uppercase tracking-widest mb-5"><Sparkles className="h-3 w-3 mr-1" />Mock tests by Aakash + Unacademy pros</Badge>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02]">
              Toppers didn't guess.<br />
              <span className="text-amber-300">They rehearsed.</span>
            </h1>
            <p className="text-slate-300 text-lg mt-6 max-w-2xl leading-relaxed">
              Pinslib Mock Tests gave last year's rankers the one thing a classroom can't — timed, graded, honest reps under exam pressure. Here's who made it to the top.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to={dest}><Button size="lg" data-testid="home-hero-cta" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">{user ? "Open dashboard" : "Create account · ₹69 one-time"}<ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
              <Link to="#toppers" className="text-sm text-slate-300 hover:text-white underline-offset-4 hover:underline">See our rankers →</Link>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-10 max-w-md">
              {[
                { id: "rankers", v: "350+", l: "Rankers" },
                { id: "mock-tests", v: "35", l: "Exams · Mock tests" },
                { id: "monthly-price", v: "₹69", l: "per month" },
              ].map((x, i) => (
                <div
                  key={x.id}
                  data-testid={`home-stat-${x.id}`}
                  className="border border-white/10 rounded-xl px-4 py-3 bg-white/5"
                >
                  <div className="font-heading text-2xl font-bold text-amber-300">{x.v}</div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">{x.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Toppers */}
      <section id="toppers" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 fade-up">
          <div>
            <div className="small-label flex items-center gap-2"><Award className="h-3 w-3" />Our Rankers · Last Year</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-2">Students who trained<br />on Pinslib — and ranked.</h2>
          </div>
          <p className="text-slate-500 max-w-sm text-sm leading-relaxed">
            Real names, real ranks, real exam years. Every one of them took their mocks here before taking the real paper.
          </p>
        </div>

        {toppers.length === 0 ? (
          <div className="mt-10 border border-dashed border-slate-300 rounded-xl p-16 text-center text-slate-500" data-testid="home-no-toppers">
            Toppers coming soon.
          </div>
        ) : (
          <div className="mt-10 space-y-14">
            {Object.entries(byExam).map(([exam, list]) => (
              <div key={exam} data-testid={`toppers-section-${exam}`}>
                <div className="flex items-center gap-3 mb-5">
                  <Badge className={`${EXAM_COLORS[exam] || "bg-slate-100 text-slate-800"} hover:${EXAM_COLORS[exam] || "bg-slate-100"} font-mono text-[11px] uppercase tracking-widest`}>{exam}</Badge>
                  <span className="text-sm text-slate-500">{list.length} ranker{list.length > 1 ? "s" : ""}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                  {list.map((t, i) => <TopperCard key={t.id} t={t} i={i} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Pricing comparison — running numbers (removed from here, moved above hero) */}

      {/* Why Pinslib */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <div className="small-label fade-up">Why they chose Pinslib</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-2 fade-up max-w-3xl">Everything a real exam does — just a lot sooner.</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {[
              { icon: Clock, title: "Exam-grade timer", desc: "The clock starts when you do, auto-submits when it ends. Just like the real paper." },
              { icon: Target, title: "Honest scoring", desc: "No vanity marks. Pass percentage, section-wise mistakes, every wrong answer explained." },
              { icon: Library, title: "Backed by libraries", desc: "Pinsec Academy students sign in with their library account — admin keeps the roster honest." },
            ].map((f, i) => (
              <div key={f.title} className="fade-up border border-slate-200 rounded-2xl p-6 hover:border-slate-900 transition-colors" style={{ animationDelay: `${i * 100}ms` }}>
                <div className="h-10 w-10 rounded-lg bg-[#EFF6FF] text-[#1D4ED8] grid place-items-center"><f.icon className="h-5 w-5" /></div>
                <div className="font-heading text-lg font-semibold mt-4">{f.title}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Next year's rank starts<br />with this year's mocks.</h2>
          <p className="text-slate-400 mt-5 max-w-xl mx-auto">Create an account, pay the one-time ₹69 activation, and start sitting the paper before the paper.</p>
          <Link to={dest} className="inline-block mt-8">
            <Button size="lg" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="home-footer-cta">
              {user ? "Open dashboard" : "Create account"}<ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500">
          <Logo />
          <div>© {new Date().getFullYear()} Pinslib Mock Tests · Pinsec Academy</div>
        </div>
      </footer>
    </div>
  );
}
